import { makeAutoObservable } from "mobx";
import { getEnv } from "mobx-easy";
import { StoreState } from "../../../enum/StoreState";
import { ILocation, ILocationFormValues } from "../../../pages/Location/types/";
import { RootEnv } from "../../setup/create-store";
import i18n from "../../../i18n";
import { LatLngLiteral } from "leaflet";
import { IStation } from "../oceanWeatherIntelligences/oceanWeatherIntelligenceModel";

export default class LocationStore {
  locations: ILocation[] = [];
  areas: ILocation[] = [];
  selectedLocation: ILocation | null = null;
  selectedCoordinates: LatLngLiteral[] = [];
  createFormValues: ILocationFormValues | null = null;
  isGeofenceLoadedByGeoJson: boolean = false;
  state: string = StoreState.DONE;
  tableState: string = StoreState.DONE;
  drawerState: string = StoreState.DONE;
  messageError: string = "";
  isDrawerOpen: boolean = false;
  isStationModalOpen: boolean = false;
  isEditing: boolean = false;
  totalLocations: number = 0;
  currentPage: number = 0;
  pageSize: number = 10;
  searchTerm: string = "";
  editStation: IStation | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  setLocations(locations: ILocation[]) {
    this.locations = locations;
  }

  setAreas(areas: ILocation[]) {
    this.areas = areas;
  }

  setSelectedLocation(location: ILocation | null) {
    this.selectedLocation = location;
  }

  setTotalLocations(total: number) {
    this.totalLocations = total;
  }

  setCurrentPage(page: number) {
    this.currentPage = page;
  }

  setPageSize(size: number) {
    this.pageSize = size;
  }

  setSearchTerm(term: string) {
    this.searchTerm = term;
  }

  setState(state: StoreState) {
    this.state = state;
  }

  setTableState(state: StoreState) {
    this.tableState = state;
  }

  setDrawerState(state: StoreState) {
    this.drawerState = state;
  }

  setMessageError(message: string) {
    this.messageError = message;
  }

  setDrawerOpen(isOpen: boolean) {
    this.isDrawerOpen = isOpen;
  }

  setIsEditing(isEditing: boolean) {
    this.isEditing = isEditing;
  }

  setSelectedCoordinates(value: LatLngLiteral[][] | LatLngLiteral[]) {
    if (value?.length > 0 && Array.isArray(value[0])) {
      this.selectedCoordinates = (value as LatLngLiteral[][]).flat();
    } else {
      this.selectedCoordinates = value as LatLngLiteral[];
    }
  }

  setCreateFormValues(values: ILocationFormValues | null) {
    this.createFormValues = values;
  }

  setIsGeofenceLoadedByGeoJson(isLoaded: boolean) {
    this.isGeofenceLoadedByGeoJson = isLoaded;
  }

  setIsStationModalOpen(isOpen: boolean) {
    this.isStationModalOpen = isOpen;
  }

  setEditStation(station: IStation | null) {
    this.editStation = station;
  }

  reset() {
    this.setState(StoreState.DONE);
    this.setTableState(StoreState.DONE);
    this.setDrawerState(StoreState.DONE);
    this.setMessageError("");
    this.setLocations([]);
    this.setAreas([]);
    this.setSelectedLocation(null);
    this.setSelectedCoordinates([]);
    this.setCreateFormValues(null);
    this.setDrawerOpen(false);
    this.setIsStationModalOpen(false);
    this.setIsEditing(false);
    this.setTotalLocations(0);
    this.setCurrentPage(0);
    this.setSearchTerm("");
    this.setEditStation(null);
  }

  async getLocationsAPI() {
    const { locationService } = getEnv<RootEnv>();
    this.setMessageError("");
    try {
      this.setTableState(StoreState.PEDDING);

      const result = await locationService.getLocations({
        limit: this.pageSize,
        offset: this.currentPage * this.pageSize,
        searchTerm: this.searchTerm,
      });

      this.setLocations(result.items);
      this.setTotalLocations(result.totalCount);
      this.setTableState(StoreState.DONE);
    } catch (error) {
      this.setTableState(StoreState.ERROR);
      this.setMessageError(i18n.t("locations.errorLoadingLocations"));
    }
  }

  async getAllLocationsAPI() {
    const { locationService } = getEnv<RootEnv>();
    this.setMessageError("");
    try {
      this.setDrawerState(StoreState.PEDDING);

      const locations = await locationService.getAllLocations();

      this.setAreas(locations);
      this.setDrawerState(StoreState.DONE);
    } catch (error) {
      this.setDrawerState(StoreState.ERROR);
      this.setMessageError(i18n.t("locations.errorLoadingLocations"));
    }
  }

  async getLocationByIdAPI(id: number) {
    if (!id || id === undefined || id === null) {
      throw new Error(`Invalid location ID: ${id}`);
    }

    const { locationService } = getEnv<RootEnv>();
    try {
      this.setDrawerState(StoreState.PEDDING);
      const location = await locationService.getLocation(id);
      this.setSelectedLocation(location);
      this.setSelectedCoordinates(location.coordinates || []);
      this.setDrawerState(StoreState.DONE);
      return location;
    } catch (error) {
      this.setDrawerState(StoreState.ERROR);
      this.setMessageError(i18n.t("locations.errorLoadingLocation"));
      throw error;
    }
  }

  async createLocationAPI(locationData: ILocationFormValues) {
    const { locationService } = getEnv<RootEnv>();
    this.setMessageError("");
    try {
      this.setDrawerState(StoreState.PEDDING);
      const newLocation = await locationService.createLocation(locationData);
      this.setLocations([newLocation, ...this.locations]);
      this.setAreas([newLocation, ...this.areas]);
      this.setTotalLocations(this.totalLocations + 1);
      this.setDrawerState(StoreState.DONE);
      return newLocation;
    } catch (error: any) {
      this.setDrawerState(StoreState.ERROR);
      if (error?.response?.status === 409) {
        this.setMessageError(
          i18n.t("locations.errorCreatingLocationNameInUse")
        );
      } else {
        this.setMessageError(i18n.t("locations.errorCreatingLocation"));
      }
      throw error;
    }
  }

  async updateLocationAPI(id: number, locationData: ILocationFormValues) {
    const { locationService } = getEnv<RootEnv>();
    this.setMessageError("");
    try {
      this.setDrawerState(StoreState.PEDDING);
      const updateResult = await locationService.updateLocation(
        id,
        locationData
      );

      if (updateResult.success) {
        const updatedLocation = await locationService.getLocation(id);
        const updatedLocations = this.locations.map((location) =>
          location.id === id ? updatedLocation : location
        );
        this.setLocations(updatedLocations);
        this.setDrawerState(StoreState.DONE);
        return updatedLocation;
      } else {
        throw new Error("Update was not successful");
      }
    } catch (error: any) {
      this.setDrawerState(StoreState.ERROR);
      if (error?.response?.status === 409) {
        this.setMessageError(
          i18n.t("locations.errorCreatingLocationNameInUse")
        );
      } else {
        this.setMessageError(i18n.t("locations.errorUpdatingLocation"));
      }
      throw error;
    }
  }

  async deleteLocationAPI(id: number) {
    const { locationService } = getEnv<RootEnv>();
    this.setMessageError("");
    try {
      this.setTableState(StoreState.PEDDING);
      await locationService.deleteLocation(id);
      this.setLocations(
        this.locations.filter((location) => location.id !== id)
      );
      this.setTotalLocations(this.totalLocations - 1);
      this.setTableState(StoreState.DONE);
    } catch (error: any) {
      this.setTableState(StoreState.ERROR);
      if (error?.response?.status === 409) {
        this.setMessageError(i18n.t("locations.errorDeletingLocationInUse"));
      } else {
        this.setMessageError(i18n.t("locations.errorDeletingLocation"));
      }
    }
  }
}
